import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Calendar, Clock, MapPin, Users, Video, ExternalLink, 
  Filter, ChevronRight
} from 'lucide-react';
import { PurchaseButton } from '@/components/CheckoutModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventCard = ({ event, index }) => {
  const eventDate = new Date(event.date);
  const isPast = eventDate < new Date();
  const month = eventDate.toLocaleString('default', { month: 'short' }).toUpperCase();
  const day = eventDate.getDate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className={`group rounded-2xl overflow-hidden glass-light card-hover ${isPast ? 'opacity-60' : ''}`}
      data-testid={`event-card-${event.id}`}
    >
      <div className="flex flex-col md:flex-row">
        {/* Date Badge */}
        <div className="md:w-32 p-6 flex flex-row md:flex-col items-center justify-center bg-primary/20 text-center">
          <span className="text-sm font-semibold text-primary">{month}</span>
          <span className="text-4xl font-bold text-white mx-2 md:mx-0">{day}</span>
          {isPast && (
            <span className="text-xs text-muted-foreground mt-1">Past Event</span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${
              event.event_type === 'conference' ? 'bg-purple-500/20 text-purple-400' :
              event.event_type === 'workshop' ? 'bg-blue-500/20 text-blue-400' :
              event.event_type === 'webinar' ? 'bg-green-500/20 text-green-400' :
              'bg-orange-500/20 text-orange-400'
            }`}>
              {event.event_type}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              event.price === 'Free' ? 'bg-green-500/20 text-green-400' : 'bg-cta/20 text-cta'
            }`}>
              {event.price}
            </span>
          </div>

          <h3 className="text-xl font-serif font-semibold text-white mb-2">
            {event.title}
          </h3>

          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
            {event.description}
          </p>

          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {event.time} {event.timezone}
            </div>
            {event.duration && (
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {event.duration}
              </div>
            )}
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {event.location}
            </div>
            {event.platform && (
              <div className="flex items-center gap-1">
                <Video className="w-4 h-4" />
                {event.platform}
              </div>
            )}
          </div>

          {!isPast && (
            <PurchaseButton
              item={event}
              itemType="event"
            />
          )}
        </div>

        {/* Image (if available) */}
        {event.image_url && (
          <div className="hidden lg:block w-64 relative">
            <img
              src={event.image_url}
              alt={event.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-card" />
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all');
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    axios.get(`${API}/events`)
      .then(res => {
        setEvents(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  const eventTypes = ['all', ...new Set(events.map(e => e.event_type))];

  const now = new Date();
  const filteredEvents = events
    .filter(event => {
      const typeMatch = selectedType === 'all' || event.event_type === selectedType;
      const dateMatch = showPast || new Date(event.date) >= now;
      return typeMatch && dateMatch;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const upcomingCount = events.filter(e => new Date(e.date) >= now).length;
  const pastCount = events.filter(e => new Date(e.date) < now).length;

  return (
    <div className="min-h-screen pt-24" data-testid="events-page">
      {/* Hero Section */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 animated-gradient opacity-20" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary mb-6">
              <Calendar className="w-4 h-4" />
              <span className="text-sm font-medium">{upcomingCount} Upcoming Events</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6" data-testid="events-title">
              Events & Workshops
            </h1>
            <p className="text-xl text-muted-foreground">
              Join us for live workshops, webinars, and conferences. Learn, connect, and grow with The Matrix University community.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-4 border-b border-border">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Filter className="w-5 h-5" />
                <span className="text-sm font-medium">Filter:</span>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {eventTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => setSelectedType(type)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all capitalize ${
                      selectedType === type
                        ? 'bg-primary text-white'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                    data-testid={`filter-type-${type}`}
                  >
                    {type === 'all' ? 'All Events' : type}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => setShowPast(!showPast)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                showPast
                  ? 'bg-secondary text-white'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {showPast ? `Hide Past Events (${pastCount})` : `Show Past Events (${pastCount})`}
            </button>
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="py-16">
        <div className="container-custom">
          {loading ? (
            <div className="space-y-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-2xl glass-light animate-pulse h-48" />
              ))}
            </div>
          ) : filteredEvents.length > 0 ? (
            <div className="space-y-6">
              {filteredEvents.map((event, index) => (
                <EventCard key={event.id} event={event} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground mb-4">No events found matching your filters.</p>
              <button
                onClick={() => { setSelectedType('all'); setShowPast(true); }}
                className="text-primary hover:underline"
              >
                Show all events
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-card/50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl font-serif font-bold text-white mb-6">
              Never Miss an Event
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Contact us to stay updated on upcoming workshops, webinars, and conferences.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
              Get Event Updates
              <ChevronRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
